/**
 * Two charts, drawn as inline SVG.
 *
 * **No charting library**, and that is a decision rather than an omission. Plan
 * D8 declined a component library and every state library beyond React Query;
 * adding ~150KB of Chart.js to draw a handful of bars would be the same trade it
 * refused. These two forms cover every panel the dashboard has, and an SVG the
 * repository owns can be themed with the application's own tokens.
 *
 * Both follow the same fixed specs, so a bar means the same thing wherever it
 * appears:
 *
 *   - marks capped at 24px, with a 4px rounded data-end and a square baseline;
 *   - a 2px surface gap between touching bars, so neighbours separate without a
 *     stroke around them — a border would add ink that is not data;
 *   - hairline solid gridlines, never dashed, always recessive;
 *   - **text never wears the series colour.** Values and labels are ink; a
 *     coloured mark beside them carries identity. A hue chosen to be legible as
 *     a fill is not legible as 11px text;
 *   - a legend whenever there are two series, so identity is never colour alone;
 *   - a hover tooltip, because an SVG chart on a screen is interactive whether
 *     or not you plan for it;
 *   - a visually-hidden table, so the figures are reachable without seeing them.
 *
 * The palette is two slots from the validated categorical set, checked against
 * this application's white surface rather than assumed: worst-pair CVD ΔE 24.7,
 * normal-vision ΔE 33.6, both clearing 3:1 contrast.
 */

import { useId, useState } from 'react';
import styles from './Charts.module.css';

/** Where a tooltip should appear, in the plot's own pixel space. */
interface Hover {
  x: number;
  y: number;
  label: string;
  detail: string;
}

/**
 * A bar rounded on the data-end only, square where it meets the baseline.
 *
 * `rx` on a `<rect>` rounds all four corners, which detaches the mark from its
 * baseline and makes short bars look like pills — so the shape is a path.
 */
function horizontalBarPath(x: number, y: number, width: number, height: number): string {
  const r = Math.max(0, Math.min(4, width, height / 2));
  if (width <= 0) return '';
  return [
    `M${x},${y}`,
    `H${x + width - r}`,
    `A${r},${r} 0 0 1 ${x + width},${y + r}`,
    `V${y + height - r}`,
    `A${r},${r} 0 0 1 ${x + width - r},${y + height}`,
    `H${x}`,
    'Z',
  ].join(' ');
}

function columnPath(x: number, top: number, width: number, baseline: number): string {
  const height = baseline - top;
  const r = Math.max(0, Math.min(4, width / 2, height));
  if (height <= 0) return '';
  return [
    `M${x},${baseline}`,
    `V${top + r}`,
    `A${r},${r} 0 0 1 ${x + r},${top}`,
    `H${x + width - r}`,
    `A${r},${r} 0 0 1 ${x + width},${top + r}`,
    `V${baseline}`,
    'Z',
  ].join(' ');
}

/** A clean upper bound, so axis ticks land on numbers a person would choose. */
function niceCeiling(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  return Math.ceil(value / magnitude) * magnitude;
}

// ---------------------------------------------------------------------------
// Horizontal bars — one or two series
// ---------------------------------------------------------------------------

export interface BarChartRow {
  label: string;
  /** One value per series, in the same order as `series`. */
  values: number[];
}

export interface BarChartProps {
  rows: BarChartRow[];
  /**
   * Series names. One name renders no legend — the card title already says what
   * is plotted, and a one-swatch box restates it.
   */
  series: string[];
  /** Describes the figure for a screen reader, e.g. "Volunteers by status". */
  caption: string;
  /** Renders a value for labels and the tooltip. Defaults to the plain number. */
  formatValue?: (value: number) => string;
  emptyMessage?: string;
}

const BAR_MAX = 24;
const BAR_GAP = 2;
const LABEL_WIDTH = 132;
const VALUE_GUTTER = 52;

/**
 * Characters that fit the label gutter at 12px.
 *
 * `LABEL_WIDTH - 10` of room at roughly 6.4px per character. Measured rather
 * than guessed: "Reading Session 04 - Demo School A" is 218px and would start
 * 96px outside the viewBox.
 */
const LABEL_CHARS = Math.floor((LABEL_WIDTH - 10) / 6.4);

/** Ellipsis on a word boundary where there is one, so the truncation reads. */
function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

export function BarChart({
  rows,
  series,
  caption,
  formatValue = (value) => String(value),
  emptyMessage = 'Nothing to show yet.',
}: BarChartProps) {
  const titleId = useId();
  const [hover, setHover] = useState<Hover | null>(null);

  if (rows.length === 0) return <p className={styles.empty}>{emptyMessage}</p>;

  const max = niceCeiling(Math.max(1, ...rows.flatMap((row) => row.values)));
  const groupHeight = Math.min(BAR_MAX, 20) * series.length + BAR_GAP * (series.length - 1);
  const rowHeight = groupHeight + 18;
  const height = rows.length * rowHeight;
  const width = 520;
  const plotWidth = width - LABEL_WIDTH - VALUE_GUTTER;

  return (
    <figure className={styles.figure} aria-labelledby={titleId}>
      <span id={titleId} className="visually-hidden">
        {caption}
      </span>

      {series.length > 1 && (
        <div className={styles.legend}>
          {series.map((name, index) => (
            <span key={name} className={styles.legendItem}>
              <span
                className={`${styles.legendSwatch} ${index === 0 ? styles.legendSwatch1 : styles.legendSwatch2}`}
                aria-hidden="true"
              />
              {name}
            </span>
          ))}
        </div>
      )}

      <div className={styles.plot}>
        <svg
          className={styles.svg}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={caption}
          onMouseLeave={() => setHover(null)}
        >
          {rows.map((row, rowIndex) => {
            const top = rowIndex * rowHeight;
            const barHeight = (groupHeight - BAR_GAP * (series.length - 1)) / series.length;

            return (
              <g key={row.label}>
                <text
                  className={styles.categoryText}
                  x={LABEL_WIDTH - 10}
                  y={top + groupHeight / 2}
                  textAnchor="end"
                  dominantBaseline="central"
                >
                  {/* Truncated, never clipped. An activity name is easily wider
                      than the gutter, and the SVG does not clip its overflow, so
                      the untruncated text would spill out of the card. The whole
                      name stays reachable in the title and the table. */}
                  <title>{row.label}</title>
                  {truncate(row.label, LABEL_CHARS)}
                </text>

                {row.values.map((value, seriesIndex) => {
                  const barTop = top + seriesIndex * (barHeight + BAR_GAP);
                  const barWidth = (value / max) * plotWidth;
                  const tipX = LABEL_WIDTH + barWidth;

                  return (
                    <g key={series[seriesIndex]}>
                      <path
                        className={seriesIndex === 0 ? styles.series1 : styles.series2}
                        d={horizontalBarPath(LABEL_WIDTH, barTop, barWidth, barHeight)}
                      />
                      {/* Value at the tip, in ink. */}
                      <text
                        className={styles.valueText}
                        x={tipX + 8}
                        y={barTop + barHeight / 2}
                        dominantBaseline="central"
                      >
                        {formatValue(value)}
                      </text>
                      {/* Hit target spans the whole row band, not just the mark:
                          a bar of width 0 is otherwise impossible to hover. */}
                      <rect
                        className={styles.hitArea}
                        x={LABEL_WIDTH}
                        y={barTop}
                        width={plotWidth}
                        height={barHeight}
                        onMouseEnter={() =>
                          setHover({
                            x: ((LABEL_WIDTH + plotWidth / 2) / width) * 100,
                            y: (barTop / height) * 100,
                            label: row.label,
                            detail:
                              series.length > 1
                                ? `${series[seriesIndex]}: ${formatValue(value)}`
                                : formatValue(value),
                          })
                        }
                      />
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>

        {hover && (
          <div className={styles.tooltip} style={{ left: `${hover.x}%`, top: `${hover.y}%` }}>
            <span className={styles.tooltipLabel}>{hover.label}</span>
            <br />
            {hover.detail}
          </div>
        )}
      </div>

      <DataTable caption={caption} rows={rows} series={series} formatValue={formatValue} />
    </figure>
  );
}

// ---------------------------------------------------------------------------
// Columns over time — one series
// ---------------------------------------------------------------------------

export interface ColumnChartProps {
  points: { label: string; value: number }[];
  caption: string;
  formatValue?: (value: number) => string;
  emptyMessage?: string;
}

/**
 * Columns rather than a line.
 *
 * A month is a discrete bucket, not a reading on a continuum, and a line implies
 * the value moved smoothly between two of them. Columns are also honest with a
 * single month of data, where a line has nothing to join and renders as either
 * one dot or nothing at all.
 */
export function ColumnChart({
  points,
  caption,
  formatValue = (value) => String(value),
  emptyMessage = 'Nothing to show yet.',
}: ColumnChartProps) {
  const titleId = useId();
  const [hover, setHover] = useState<Hover | null>(null);

  if (points.length === 0) return <p className={styles.empty}>{emptyMessage}</p>;

  const width = 520;
  const height = 200;
  const padTop = 12;
  const padBottom = 28;
  const padLeft = 48;
  const baseline = height - padBottom;
  const plotWidth = width - padLeft;
  const max = niceCeiling(Math.max(1, ...points.map((point) => point.value)));

  const band = plotWidth / points.length;
  const barWidth = Math.min(BAR_MAX, band - 12);
  const ticks = [0, max / 2, max];

  return (
    <figure className={styles.figure} aria-labelledby={titleId}>
      <span id={titleId} className="visually-hidden">
        {caption}
      </span>

      <div className={styles.plot}>
        <svg
          className={styles.svg}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={caption}
          onMouseLeave={() => setHover(null)}
        >
          {ticks.map((tick) => {
            const y = baseline - (tick / max) * (baseline - padTop);
            return (
              <g key={tick}>
                <line className={styles.gridline} x1={padLeft} x2={width} y1={y} y2={y} />
                <text
                  className={styles.axisText}
                  x={padLeft - 8}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="central"
                >
                  {formatValue(tick)}
                </text>
              </g>
            );
          })}

          <line className={styles.baseline} x1={padLeft} x2={width} y1={baseline} y2={baseline} />

          {points.map((point, index) => {
            const x = padLeft + index * band + (band - barWidth) / 2;
            const top = baseline - (point.value / max) * (baseline - padTop);

            return (
              <g key={point.label}>
                <path className={styles.series1} d={columnPath(x, top, barWidth, baseline)} />
                <text
                  className={styles.axisText}
                  x={x + barWidth / 2}
                  y={baseline + 16}
                  textAnchor="middle"
                >
                  {point.label}
                </text>
                <rect
                  className={styles.hitArea}
                  x={padLeft + index * band}
                  y={padTop}
                  width={band}
                  height={baseline - padTop}
                  onMouseEnter={() =>
                    setHover({
                      x: ((x + barWidth / 2) / width) * 100,
                      y: (top / height) * 100,
                      label: point.label,
                      detail: formatValue(point.value),
                    })
                  }
                />
              </g>
            );
          })}
        </svg>

        {hover && (
          <div className={styles.tooltip} style={{ left: `${hover.x}%`, top: `${hover.y}%` }}>
            <span className={styles.tooltipLabel}>{hover.label}</span>
            <br />
            {hover.detail}
          </div>
        )}
      </div>

      <DataTable
        caption={caption}
        rows={points.map((point) => ({ label: point.label, values: [point.value] }))}
        series={['Value']}
        formatValue={formatValue}
      />
    </figure>
  );
}

/**
 * The figures, reachable without seeing the chart.
 *
 * Visually hidden rather than behind a toggle: a control nobody presses is not
 * an accessibility feature, and this content is small enough to always ship.
 */
function DataTable({
  caption,
  rows,
  series,
  formatValue,
}: {
  caption: string;
  rows: BarChartRow[];
  series: string[];
  formatValue: (value: number) => string;
}) {
  return (
    <table className="visually-hidden">
      <caption>{caption}</caption>
      <thead>
        <tr>
          <th scope="col">Category</th>
          {series.map((name) => (
            <th key={name} scope="col">
              {name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <th scope="row">{row.label}</th>
            {row.values.map((value, index) => (
              <td key={series[index]}>{formatValue(value)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Donut — part-to-whole
// ---------------------------------------------------------------------------

export interface PieSlice {
  label: string;
  value: number;
}

export interface PieChartProps {
  slices: PieSlice[];
  caption: string;
  /** What the centre total counts, e.g. "volunteers". Kept short — it is 10px. */
  totalLabel: string;
  emptyMessage?: string;
}

/** The prototype's doughnut proportions: a 60% cutout. */
const DONUT_CUTOUT = 0.6;
const PIE_SIZE = 168;

/**
 * A donut, for a breakdown whose parts genuinely sum to the whole.
 *
 * **Only use this when they do.** A pie asserts that its slices are the entire
 * quantity and that each is a share of it; given two overlapping measures it
 * invents a total that does not exist and prints confident percentages of it.
 * If the parts can double-count, or one can exceed the other, the honest form is
 * a bar.
 *
 * Three things stop it being a guessing game:
 *
 *   - the whole sits in the middle, because a pie otherwise never says what
 *     100% is;
 *   - the legend carries the value *and* the share as text, so nothing has to be
 *     estimated from an angle;
 *   - zero-value slices are dropped from the ring, since a slice of no width is
 *     an invisible legend entry — but they stay in the table, because "none" is
 *     a different statement from "not applicable".
 */
export function PieChart({
  slices,
  caption,
  totalLabel,
  emptyMessage = 'Nothing to show yet.',
}: PieChartProps) {
  const titleId = useId();
  const [hover, setHover] = useState<Hover | null>(null);

  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  const drawn = slices.filter((slice) => slice.value > 0);

  if (total === 0) return <p className={styles.empty}>{emptyMessage}</p>;

  const centre = PIE_SIZE / 2;
  const radius = centre - 4;
  const inner = radius * DONUT_CUTOUT;

  let angle = -Math.PI / 2; // start at twelve o'clock

  return (
    <figure className={styles.figure} aria-labelledby={titleId}>
      <span id={titleId} className="visually-hidden">
        {caption}
      </span>

      <div className={styles.pieWrap}>
        <div className={styles.plot}>
          <svg
            className={styles.pieSvg}
            viewBox={`0 0 ${PIE_SIZE} ${PIE_SIZE}`}
            role="img"
            aria-label={caption}
            onMouseLeave={() => setHover(null)}
          >
            {drawn.map((slice, index) => {
              const sweep = (slice.value / total) * Math.PI * 2;
              const start = angle;
              const end = angle + sweep;
              angle = end;

              return (
                <path
                  key={slice.label}
                  className={`${sliceClass(index)} ${styles.sliceRing}`}
                  d={donutSegment(centre, radius, inner, start, end)}
                  onMouseEnter={() =>
                    setHover({
                      x: 50,
                      y: 0,
                      label: slice.label,
                      detail: `${slice.value} — ${share(slice.value, total)}`,
                    })
                  }
                >
                  <title>{`${slice.label}: ${slice.value} (${share(slice.value, total)})`}</title>
                </path>
              );
            })}

            {/* The whole. A donut that does not name its total asks the reader
                to take the percentages on trust. */}
            <text className={styles.pieCentreValue} x={centre} y={centre - 4} textAnchor="middle">
              {total}
            </text>
            <text className={styles.pieCentreLabel} x={centre} y={centre + 14} textAnchor="middle">
              {totalLabel}
            </text>
          </svg>

          {hover && (
            <div className={styles.tooltip} style={{ left: `${hover.x}%`, top: `${hover.y}%` }}>
              <span className={styles.tooltipLabel}>{hover.label}</span>
              <br />
              {hover.detail}
            </div>
          )}
        </div>

        <div className={styles.pieLegend}>
          {slices.map((slice, index) => (
            <span key={slice.label} className={styles.pieLegendRow}>
              <span className={`${styles.legendSwatch} ${swatchClass(index)}`} aria-hidden="true" />
              <span className={styles.pieLegendLabel}>{slice.label}</span>
              <span className={styles.pieLegendValue}>
                {slice.value} · {share(slice.value, total)}
              </span>
            </span>
          ))}
        </div>
      </div>

      <DataTable
        caption={caption}
        rows={slices.map((slice) => ({ label: slice.label, values: [slice.value] }))}
        series={[totalLabel]}
        formatValue={(value) => String(value)}
      />
    </figure>
  );
}

function share(value: number, total: number): string {
  if (total <= 0) return '—';
  return `${Math.round((value / total) * 100)}%`;
}

/** Slots are assigned in fixed order and never cycled — a 5th folds to "Other". */
function sliceClass(index: number): string {
  return [styles.slice1, styles.slice2, styles.slice3, styles.slice4][index % 4] as string;
}

function swatchClass(index: number): string {
  return [styles.legendSwatch1, styles.legendSwatch2, styles.legendSwatch3, styles.legendSwatch4][
    index % 4
  ] as string;
}

/** One ring segment, as a path: outer arc out, inner arc back. */
function donutSegment(
  centre: number,
  radius: number,
  inner: number,
  start: number,
  end: number
): string {
  // A full circle cannot be drawn as a single arc — the start and end points
  // coincide and the path collapses. Two half-sweeps render it correctly.
  if (end - start >= Math.PI * 2 - 1e-6) {
    const half = start + Math.PI;
    return `${donutSegment(centre, radius, inner, start, half)} ${donutSegment(centre, radius, inner, half, start + Math.PI * 2)}`;
  }

  const large = end - start > Math.PI ? 1 : 0;
  const p = (r: number, a: number) => `${centre + r * Math.cos(a)},${centre + r * Math.sin(a)}`;

  return [
    `M${p(radius, start)}`,
    `A${radius},${radius} 0 ${large} 1 ${p(radius, end)}`,
    `L${p(inner, end)}`,
    `A${inner},${inner} 0 ${large} 0 ${p(inner, start)}`,
    'Z',
  ].join(' ');
}
